import React, { Component } from "react";
import { compose } from "redux";
import { bool, arrayOf, string } from "prop-types";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import CssBaseline from "@mui/material/CssBaseline";
import { withTranslation } from "react-i18next";

import Header from "components/common/header";
import Footer from "components/common/footer";
import AgentServices from "components/agent-services";
import Alert from "components/common/alert";
import Auth from "components/auth";
import Claims from "components/claims";
import Home from "components/home";
import Homeowner from "components/homeowner";
import LoadingSpinner from "components/common/loading-spinner";
import NotFound from "components/not-found";
import AgentOrders from "components/agent-orders";
import GetStarted from "components/get-started";
import OrderConfirmation from "components/common/confirm-order";
import RenewPolicy from "components/homeowner/orders/plan-builder/renew-policy";
import RenewPolicyCheckout from "components/homeowner/orders/renew-policy-checkout/renew-policy-checkout";
import BrochurePage from "components/home/brochure";
import PrivacyPolicy from "components/privacy-policy";
import TermsAndConditions from "components/terms-and-conditions";

// Static Pages
// import Maintenanace from "components/maintenance";
// import AboutUs from "components/about-us";
// import Contact from "components/contact";
// import SinglePlaylist from "components/maintenance/singlePlaylist";

import { getErrors, getIsLoading } from "store/reducers";
import { dismissError } from "store/actions/app";

import { AppContainer, AppContent } from "./styles";
import withRouter from "helpers/withRouters";
import { getSubdomain } from "helpers/utils";

class App extends Component {
  static propTypes = {
    isLoading: bool.isRequired,
    errors: arrayOf(string),
  };

  subdomain = getSubdomain() || "main";
  state = { show: false };

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
    localStorage.setItem("maintenance", "true"); 
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    if (this.props.location.pathname !== "/") return;
    const breakpoint = window.innerHeight * 0.25;
    this.setState({ show: window.scrollY >= breakpoint });
  };

  handleErrorDismissal = (message, index) => () =>
    this.props.dispatchErrorDismissal(message, index);

  render() {
    const { isLoading, errors } = this.props;
    const error = errors.length ? errors[0] : null;
    const showHeader =
      this.props.location.pathname === "/" ? this.state.show : true;

    // Subdomain logic
    const isApp = this.subdomain === process.env.REACT_APP_SUBDOMAIN || "order";
    return (
      <AppContainer>
        <CssBaseline />
        <Header
          transparent={!showHeader}
          handleNavigation={this.props.navigate}
        />

        <AppContent>
          <Routes>
            {/* ----------- STATIC PAGES ----------- */}
            {/* 
                <Route path="/about" element={<AboutUs />} />
                <Route
                  path="/contact"
                  element={<Contact />}
                />
                <Route path="/maintenance"  element={<Maintenanace />} />
                <Route
                  path="/maintenance/:playlistId"
                  element={<SinglePlaylist />}
                />
                 */}

            {/* ----------- APP SUBDOMAIN ROUTES ----------- */}
            {isApp && (
              <>
                <Route
                  exact
                  path="/:locale(es)?"
                  element={<Home handleNavigation={this.props.navigate} />}
                />
                <Route
                  exact
                  path="/"
                  element={<Home handleNavigation={this.props.navigate} />}
                />
                <Route
                  path="/plans/start"
                  element={
                    <GetStarted handleNavigation={this.props.navigate} />
                  }
                />
                <Route
                  exact
                  path="/plans/order"
                  element={
                    <AgentOrders
                      location={this.props.location}
                      handleNavigation={this.props.navigate}
                    />
                  }
                />
                <Route
                  exact
                  path="/plans/order/confirm"
                  element={
                    <OrderConfirmation
                      handleNavigation={this.props.navigate}
                      locationobj={this.props.location}
                    />
                  }
                />
                <Route path="/agent" element={<AgentServices />} />
                <Route path="/agents/services" element={<AgentServices />} />
                <Route
                  path="/agent/order"
                  element={
                    <AgentOrders
                      location={this.props.location}
                      handleNavigation={this.props.navigate}
                    />
                  }
                />
                <Route
                  path="/agent/order/confirm"
                  element={
                    <OrderConfirmation
                      handleNavigation={this.props.navigate}
                      locationobj={this.props.location}
                    />
                  }
                />

                <Route path="/homeowner/*" element={<Homeowner />} />
                <Route
                  path="/homeowner/order/confirm"
                  element={
                    <OrderConfirmation
                      handleNavigation={this.props.navigate}
                      locationobj={this.props.location}
                    />
                  }
                />
                <Route
                  path="/policyRenew"
                  element={
                    <RenewPolicy
                      navigate={this.props.navigate}
                      location={this.props.location}
                    />
                  }
                />
                <Route
                  path="/policyRenew/checkout"
                  element={
                    <RenewPolicyCheckout
                      navigate={this.props.navigate}
                      location={this.props.location}
                    />
                  }
                />
                <Route
                  path="/policyRenew/checkout/confirm"
                  element={
                    <OrderConfirmation
                      handleNavigation={this.props.navigate}
                      locationobj={this.props.location}
                    />
                  }
                />

                <Route
                  path="/claims"
                  element={<Claims navigate={this.props.navigate} />}
                />

                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />

                <Route path="/auth/*" element={<Auth />} />
                <Route path="/brochure" element={<BrochurePage />} />
              </>
            )}

            <Route path="*" element={<NotFound />} />
          </Routes>

          {error && (
            <Alert
              message={error}
              onDismiss={this.handleErrorDismissal(error, 0)}
            />
          )}
          {isLoading && <LoadingSpinner />}
        </AppContent>

        <Footer />
      </AppContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  errors: getErrors(state),
  isLoading: getIsLoading(state),
});

const composed = compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps, { dispatchErrorDismissal: dismissError })
)(App);

function withMyHook(WrappedComponent) {
  return function WithMyHook(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const handlenavigate = (to, opt) => navigate(to, opt);
    return (
      <WrappedComponent
        {...props}
        navigate={handlenavigate}
        location={location}
      />
    );
  };
}

export default withMyHook(composed);
